import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useAnimation } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useParallax } from '../hooks/useParallax';

gsap.registerPlugin(ScrollTrigger);

const Portfolio = () => {
    const parallaxOffset = useParallax(0.1);
    const containerRef = useRef(null);
    const headerRef = useRef(null);
    const cardsRef = useRef(null);

    // Framer Motion animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.6,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const cardVariants = {
        hidden: { y: 50, opacity: 0, scale: 0.9 },
        visible: {
            y: 0,
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    };

    // GSAP Animations
    useEffect(() => {
        const ctx = gsap.context(() => {
            // Header animation
            gsap.fromTo(headerRef.current,
                { y: 50, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: headerRef.current,
                        start: "top 80%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            // Cards stagger animation
            gsap.fromTo(".portfolio-card",
                {
                    y: 60,
                    opacity: 0,
                    scale: 0.8,
                    rotationY: -15
                },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    rotationY: 0,
                    duration: 0.8,
                    ease: "back.out(1.7)",
                    stagger: 0.2,
                    scrollTrigger: {
                        trigger: cardsRef.current,
                        start: "top 80%",
                        toggleActions: "play none none reverse"
                    }
                }
            );

            // Floating animation for cards
            gsap.to(".portfolio-card", {
                y: -5,
                duration: 2,
                ease: "power2.inOut",
                yoyo: true,
                repeat: -1,
                stagger: 0.3
            });

        }, containerRef);

        return () => ctx.revert();
    }, []);

    // Animation controls for Framer Motion
    const controls = useAnimation();
    const isInView = useInView(containerRef, { once: true });

    useEffect(() => {
        if (isInView) {
            controls.start("visible");
        }
    }, [controls, isInView]);

    const projects = [
        {
            id: 'freelance-marketplace',
            title: 'Freelance MarketPlace — Freelancing Website',
            subtitle: 'Project Team Self',
            description: 'A freelance marketplace is a platform where clients hire skilled professionals for short-term or project-based work. It connects freelancers and businesses globally, enabling flexible and remote collaboration',
            image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80',
            tags: ['Web App', 'React', 'TailwindCSS', 'NodeJs', 'ExpressJs', 'Mongodb', 'Firebase'],
            shadowColor: 'primary'
        },
        {
            id: 'contesthub',
            title: 'ContestHub - Related with Contest',
            subtitle: 'Cloning Discord Web App',
            description: 'ContestHub is an online platform where users can participate in various contests and challenges. It provides a competitive environment to showcase skills, earn recognition, and improve performance.',
            image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2126&q=80',
            tags: ['Web App', 'JavaScript', 'React', 'tailwind', 'firebase', 'expressJs', 'Mongodb'],
            shadowColor: 'blue'
        },
        {
            id: 'toy-topia',
            title: 'Toy-Topia',
            subtitle: ' Web App ',
            description: 'ToyTopia is an online toy marketplace offering a wide range of fun and educational toys for kids. It provides a user-friendly shopping experience with secure ordering and fast delivery',
            image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80',
            tags: ['UI/UX Design', 'Figma', 'Mobile App'],
            shadowColor: 'purple'
        }
    ];

    return (
        <motion.section
            ref={containerRef}
            className="w-full mx-auto pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-black/40 relative overflow-hidden"
            id="portfolio"
            variants={containerVariants}
            initial="hidden"
            animate={controls}
        >
            {/* Animated Background Elements - Responsive */}
            <div className="absolute inset-0 opacity-10">
                <motion.div
                    className="absolute top-1/4 left-2 sm:left-10 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-primary/30 rounded-full blur-2xl sm:blur-3xl"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                        x: [0, 15, 0],
                        y: [0, -10, 0]
                    }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
                <motion.div
                    className="absolute bottom-1/4 right-2 sm:right-10 w-40 h-40 sm:w-60 sm:h-60 md:w-80 md:h-80 bg-purple-500/30 rounded-full blur-2xl sm:blur-3xl"
                    animate={{
                        scale: [1.2, 1, 1.2],
                        opacity: [0.5, 0.3, 0.5],
                        x: [0, -10, 0],
                        y: [0, 15, 0]
                    }}
                    transition={{ duration: 10, repeat: Infinity }}
                />
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                <motion.div
                    ref={headerRef}
                    className="text-center mb-8 sm:mb-12 md:mb-16 space-y-3 sm:space-y-4"
                    variants={itemVariants}
                >
                    <motion.div
                        className="inline-flex items-center gap-2 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark px-3 py-1.5 rounded-full text-xs font-medium"
                        whileHover={{ scale: 1.05 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                        <motion.span
                            className="w-2 h-2 bg-primary rounded-full"
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        />
                        Featured Work
                    </motion.div>

                    <motion.h2
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white px-4"
                        variants={itemVariants}
                    >
                        Selected <motion.span
                            className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-blue-500 to-purple-500"
                            animate={{
                                backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                            }}
                            transition={{ duration: 3, repeat: Infinity }}
                        >
                            Projects
                        </motion.span>
                    </motion.h2>

                    <motion.p
                        className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-4"
                        variants={itemVariants}
                    >
                        A showcase of my recent work in web development, UI/UX design, and full-stack applications.
                    </motion.p>
                </motion.div>

                <motion.div
                    ref={cardsRef}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8"
                    variants={containerVariants}
                >
                    {projects.map((project, index) => (
                        <motion.div
                            key={index}
                            className="portfolio-card group relative bg-surface-light dark:bg-surface-dark rounded-xl sm:rounded-2xl overflow-hidden border border-border-light dark:border-border-dark hover:border-primary/30 transition-all duration-500 touch-manipulation"
                            variants={cardVariants}
                            whileHover={{
                                y: -5,
                                scale: 1.02,
                                rotateY: window.innerWidth > 768 ? 5 : 0,
                                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                            }}
                            whileTap={{ scale: 0.98 }}
                            transition={{ type: "spring", stiffness: 300 }}
                            style={{
                                animationDelay: `${index * 0.2}s`,
                                transform: `translateY(${parallaxOffset * (index + 1) * 0.1}px)`
                            }}
                        >
                            <div className="relative overflow-hidden">
                                <motion.img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-40 sm:h-48 md:h-52 object-cover"
                                    whileHover={{ scale: window.innerWidth > 768 ? 1.1 : 1.05 }}
                                    transition={{ duration: 0.6 }}
                                />
                                <motion.div
                                    className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    initial={{ opacity: 0 }}
                                    whileHover={{ opacity: 1 }}
                                />
                                <motion.div
                                    className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-surface-light dark:bg-surface-dark rounded-full p-1.5 sm:p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    whileHover={{ scale: 1.1, rotate: 15 }}
                                >
                                    <span className="material-icons-round text-primary text-sm">launch</span>
                                </motion.div>
                            </div>

                            <motion.div
                                className="p-4 sm:p-5 md:p-6"
                                variants={itemVariants}
                            >
                                <div className="space-y-2 sm:space-y-3">
                                    <div>
                                        <motion.p
                                            className="text-xs font-medium text-primary uppercase tracking-wider mb-1"
                                            whileHover={{ scale: 1.05 }}
                                        >
                                            {project.subtitle}
                                        </motion.p>
                                        <motion.h3
                                            className="text-base sm:text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary via-blue-500 to-purple-500 leading-tight group-hover:from-purple-500 group-hover:via-primary group-hover:to-blue-500 transition-all duration-300 line-clamp-2"
                                            whileHover={{ scale: 1.02 }}
                                        >
                                            {project.title}
                                        </motion.h3>
                                    </div>

                                    <motion.p
                                        className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-3"
                                        variants={itemVariants}
                                    >
                                        {project.description}
                                    </motion.p>

                                    <div className="flex flex-wrap gap-1 pt-2">
                                        {project.tags.slice(0, window.innerWidth < 640 ? 2 : 3).map((tag, tagIndex) => (
                                            <motion.span
                                                key={tagIndex}
                                                className="px-2 py-1 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-md text-xs font-medium text-gray-600 dark:text-gray-400"
                                                whileHover={{
                                                    scale: 1.1,
                                                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                                                    y: -2
                                                }}
                                                transition={{ type: "spring", stiffness: 400 }}
                                            >
                                                {tag}
                                            </motion.span>
                                        ))}
                                        {project.tags.length > (window.innerWidth < 640 ? 2 : 3) && (
                                            <motion.span
                                                className="px-2 py-1 text-xs text-gray-500 dark:text-gray-500"
                                                whileHover={{ scale: 1.05 }}
                                            >
                                                +{project.tags.length - (window.innerWidth < 640 ? 2 : 3)} more
                                            </motion.span>
                                        )}
                                    </div>

                                    <motion.div
                                        className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 dark:border-gray-800"
                                        variants={itemVariants}
                                    >
                                        <motion.div
                                            whileHover={{ scale: 1.02, y: -2 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <Link
                                                to={`/portfolio/${project.id}`}
                                                className="w-full bg-primary text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg font-medium hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base touch-manipulation"
                                            >
                                                View Project
                                                <motion.span
                                                    className="material-icons-round text-sm"
                                                    animate={{ x: [0, 5, 0] }}
                                                    transition={{ duration: 2, repeat: Infinity }}
                                                >
                                                    arrow_forward
                                                </motion.span>
                                            </Link>
                                        </motion.div>
                                    </motion.div>
                                </div>
                            </motion.div>
                        </motion.div>
                    ))}
                </motion.div>

                <motion.div
                    className="text-center mt-12 sm:mt-16"
                    variants={itemVariants}
                >
                    <motion.div
                        whileHover={{ scale: 1.05, y: -3 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        <a
                            href="#contact"
                            className="inline-flex items-center gap-2 bg-primary text-white px-6 sm:px-8  sm:py-4 rounded-full font-semibold hover:bg-primary/90 transition-all duration-300 text-sm sm:text-base touch-manipulation"
                        >
                            View All Projects
                            <motion.span
                                className="material-icons-round text-sm"
                                animate={{ rotate: [0, 360] }}
                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            >
                                arrow_forward
                            </motion.span>
                        </a>
                    </motion.div>
                </motion.div>
            </div>
        </motion.section>
    );
};

export default Portfolio;